/**
 * Утилиты для валидации JSON Schema
 */

/**
 * Валидирует JSON Schema на соответствие стандарту JSON Schema Draft-07
 * @param {Object} schema - схема для валидации
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateJsonSchema = (schema) => {
  const errors = [];
  
  if (!schema || typeof schema !== 'object') {
    return {
      isValid: false,
      errors: ['Schema должен быть объектом']
    };
  }

  // Проверка обязательных полей
  if (!schema.type) {
    errors.push('Schema должен содержать поле "type"');
  } else if (schema.type !== 'object' && schema.type !== 'array' && schema.type !== 'string' && 
             schema.type !== 'number' && schema.type !== 'integer' && schema.type !== 'boolean' && 
             schema.type !== 'null') {
    errors.push('Поле "type" должно содержать валидный тип: object, array, string, number, integer, boolean, null');
  }

  // Если это объект, проверяем properties
  if (schema.type === 'object') {
    if (schema.properties && typeof schema.properties !== 'object') {
      errors.push('Поле "properties" должно быть объектом');
    }
    
    if (schema.required && !Array.isArray(schema.required)) {
      errors.push('Поле "required" должно быть массивом строк');
    }
  }

  // Если это массив, проверяем items
  if (schema.type === 'array') {
    if (!schema.items) {
      errors.push('Schema типа "array" должен содержать поле "items"');
    }
  }

  // Проверка числовых ограничений
  if (schema.type === 'number' || schema.type === 'integer') {
    if (schema.minimum !== undefined && typeof schema.minimum !== 'number') {
      errors.push('Поле "minimum" должно быть числом');
    }
    if (schema.maximum !== undefined && typeof schema.maximum !== 'number') {
      errors.push('Поле "maximum" должно быть числом');
    }
    if (schema.minimum !== undefined && schema.maximum !== undefined && schema.minimum > schema.maximum) {
      errors.push('Значение "minimum" не может быть больше "maximum"');
    }
  }

  // Проверка строковых ограничений
  if (schema.type === 'string') {
    if (schema.minLength !== undefined && (typeof schema.minLength !== 'number' || schema.minLength < 0)) {
      errors.push('Поле "minLength" должно быть неотрицательным числом');
    }
    if (schema.maxLength !== undefined && (typeof schema.maxLength !== 'number' || schema.maxLength < 0)) {
      errors.push('Поле "maxLength" должно быть неотрицательным числом');
    }
    if (schema.minLength !== undefined && schema.maxLength !== undefined && schema.minLength > schema.maxLength) {
      errors.push('Значение "minLength" не может быть больше "maxLength"');
    }
    if (schema.pattern && typeof schema.pattern !== 'string') {
      errors.push('Поле "pattern" должно быть строкой');
    }

    if (schema.enum && !Array.isArray(schema.enum)) {
      errors.push('Поле "enum" должно быть массивом');
    }

    if (schema.enum && Array.isArray(schema.enum)) {
      // Проверка на пустой массив
      if (schema.enum.length === 0) {
        errors.push('Поле "enum" не может быть пустым массивом');
      }
      
      // Проверка уникальности значений
      const uniqueValues = new Set(schema.enum);
      if (uniqueValues.size !== schema.enum.length) {
        errors.push('Поле "enum" содержит дублирующиеся значения');
      }
      
      // Проверка типов значений (все должны быть одного типа)
      const valueTypes = schema.enum.map(val => typeof val);
      const uniqueTypes = new Set(valueTypes);
      if (uniqueTypes.size > 1) {
        errors.push('Все значения в поле "enum" должны быть одного типа');
      }
      
      // Проверка на null/undefined значения
      if (schema.enum.some(val => val === null || val === undefined)) {
        errors.push('Поле "enum" не должно содержать null или undefined значения');
      }
    }    
  }

  // Рекурсивная валидация вложенных схем
  if (schema.properties) {
    for (const [key, propertySchema] of Object.entries(schema.properties)) {
      const propertyValidation = validateJsonSchema(propertySchema);
      if (!propertyValidation.isValid) {
        propertyValidation.errors.forEach(error => {
          errors.push(`Ошибка в свойстве "${key}": ${error}`);
        });
      }
    }
  }

  if (schema.items) {
    const itemsValidation = validateJsonSchema(schema.items);
    if (!itemsValidation.isValid) {
      itemsValidation.errors.forEach(error => {
        errors.push(`Ошибка в items: ${error}`);
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Валидирует схему на наличие циклических ссылок
 * @param {Object} schema - схема для проверки
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateSchemaCircularReferences = (schema) => {
  const errors = [];
  const visited = new Set();
  
  const checkCircular = (currentSchema, path = []) => {
    if (visited.has(currentSchema)) {
      errors.push(`Обнаружена циклическая ссылка в пути: ${path.join(' -> ')}`);
      return;
    }
    
    visited.add(currentSchema);
    
    if (currentSchema.properties) {
      for (const [key, propertySchema] of Object.entries(currentSchema.properties)) {
        checkCircular(propertySchema, [...path, key]);
      }
    }
    
    if (currentSchema.items) {
      checkCircular(currentSchema.items, [...path, 'items']);
    }
    
    visited.delete(currentSchema);
  };
  
  checkCircular(schema);
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Валидирует соответствие required полей и properties
 * @param {Object} schema - схема для проверки
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateRequiredFields = (schema) => {
  const errors = [];
  
  if (schema.type === 'object' && schema.required && schema.properties) {
    const requiredFields = schema.required;
    const availableProperties = Object.keys(schema.properties);
    
    for (const requiredField of requiredFields) {
      if (!availableProperties.includes(requiredField)) {
        errors.push(`Обязательное поле "${requiredField}" не найдено в properties`);
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Валидирует уникальность имен полей в JSON Schema
 * @param {Object} schema - схема для валидации
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array }
 */
export const validateFieldNameUniqueness = (schema) => {
  const errors = [];
  
  if (schema.type === 'object' && schema.properties) {
   
    const fieldNames = Object.keys(schema.properties)
  
    //console.log('Schema', schema.properties)
    /*
    const duplicateNames = [];
    const seenNames = new Set();
    
    // Проверка точных дубликатов
    for (const name of fieldNames) {
          console.log(name)
          console.log(duplicateNames)
          console.log(seenNames)
          //alert(55)
      if (seenNames.has(name)) {
        duplicateNames.push(name);
      } else {
        seenNames.add(name);
      }
    }
    
    console.log(duplicateNames)
    console.log(seenNames)
    //alert(77)

    if (duplicateNames.length > 0) {
      errors.push(`Обнаружены дублирующиеся имена полей: ${duplicateNames.join(', ')}`);
    }
    */

    // Точных совпадений в схеме быть не может, т.к. уникальность ключей - это базовое свойство schema.properties (как любого объекта JavaScript)

    // Проверка на похожие имена (различается только регистр)
    const lowerCaseNames = fieldNames.map(name => name.toLowerCase());
    const uniqueLowerCaseNames = new Set(lowerCaseNames);
    if (lowerCaseNames.length !== uniqueLowerCaseNames.size) {
      const caseDuplicates = [];
      const seenLowerCase = new Set();
      for (const name of fieldNames) {
        const lowerName = name.toLowerCase();
        if (seenLowerCase.has(lowerName)) {
          caseDuplicates.push(name);
        } else {
          seenLowerCase.add(lowerName);
        }
      }
      errors.push(`Обнаружены поля с похожими названиями (различается только регистр): ${caseDuplicates.join(', ')}`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Комплексная валидация JSON Schema
 * @param {Object} schema - схема для валидации
 * @returns {Object} - результат валидации { isValid: boolean, errors: Array, warnings: Array }
 */
export const validateSchemaComprehensive = (schema) => {

  console.log('Validation run on ', schema)

  const allErrors = [];
  const warnings = [];
  
  // Основная валидация
  const basicValidation = validateJsonSchema(schema);
  allErrors.push(...basicValidation.errors);
  
  // Проверка циклических ссылок
  const circularValidation = validateSchemaCircularReferences(schema);
  allErrors.push(...circularValidation.errors);
  
  // Проверка required полей
  const requiredValidation = validateRequiredFields(schema);
  allErrors.push(...requiredValidation.errors);
  
  // Проверка уникальности имен полей
  const uniquenessValidation = validateFieldNameUniqueness(schema);
  allErrors.push(...uniquenessValidation.errors);
  
  // Дополнительные проверки для предупреждений
  if (schema.type === 'object' && schema.properties) {
    const properties = Object.keys(schema.properties);
    if (properties.length === 0) {
      warnings.push('Schema содержит пустой объект properties');
    }
    
    // Проверка на дублирующиеся названия полей (с учетом регистра)
    const lowerCaseNames = properties.map(name => name.toLowerCase());
    const uniqueNames = new Set(lowerCaseNames);
    if (lowerCaseNames.length !== uniqueNames.size) {
      warnings.push('Обнаружены поля с похожими названиями (возможно, различается только регистр)');
    }
  }
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings
  };
};
